import { useCallback, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

// ============================================================================
// Component
// ============================================================================

/**
 * The Majestic Interactive Map Section.
 *
 * Forked from `@vibesites/theme-via6/components/InteractiveMapSection` to fix a
 * customer-reported scroll trap: the embedded ApartmentSync / Repli360 leasing
 * widget attaches its own wheel handlers, so scrolling over it zooms the map or
 * scrolls the internal rooms list instead of scrolling the page. Users couldn't
 * scroll back up to the top of the page once the pointer was over the widget.
 *
 * Fix: a "scroll gate" overlay (the Google-Maps-embed pattern). While the gate
 * is locked, the widget subtree has `pointer-events: none`, so wheel/scroll and
 * clicks pass straight through to the page — scrolling up (or down) past the map
 * always works. A single click on the gate unlocks the widget for full
 * interaction; moving the pointer back out of the widget re-locks it so page
 * scrolling is restored automatically. Keyboard focus is unaffected by
 * `pointer-events: none`, so keyboard users can still tab into the widget.
 */
export function InteractiveMapSection() {
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: 'apartment-sync-map',
  });

  const [interactive, setInteractive] = useState(false);
  const activate = useCallback(() => setInteractive(true), []);
  const lock = useCallback(() => setInteractive(false), []);

  return (
    <EditableToken
      path="interactivemapsection.map_block"
      as="div"
      className="min-h-[400px] md:min-h-[500px] flex flex-col bg-[var(--vibe-color-background)]"
    >
      {mapSlot ? (
        <SectionContextProvider sectionId={mapSlot.sectionId}>
          {/* Scroll-gate wrapper: re-lock when the pointer leaves the widget so
              page scrolling is always available over the map. */}
          <div className="relative flex flex-1 flex-col" onMouseLeave={lock}>
            <div
              className="flex flex-1 flex-col"
              style={{ pointerEvents: interactive ? 'auto' : 'none' }}
              aria-hidden={interactive ? undefined : true}
            >
              <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
            </div>

            {!interactive && (
              <button
                type="button"
                onClick={activate}
                className="group absolute inset-0 z-10 flex cursor-pointer items-end justify-center bg-transparent p-4 focus:outline-none"
                aria-label="Click to interact with the map. While inactive, scrolling moves the page."
              >
                <span
                  className="pointer-events-none rounded-full px-4 py-2 text-sm font-medium shadow-md opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
                  style={{
                    background: 'var(--vibe-color-surface, rgba(0,0,0,0.75))',
                    color: 'var(--vibe-color-text, #ffffff)',
                  }}
                >
                  Click to interact with the map
                </span>
              </button>
            )}
          </div>
        </SectionContextProvider>
      ) : (
        <div>No block selected</div>
      )}
    </EditableToken>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.map_block': {
      label: 'Map Block',
      type: 'block',
      defaultValue: 'apartment-sync-map',
      acceptedBlockCategories: ['Integrations'],
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapSection;
